import { useCallback, useContext, useMemo } from "react";
import { pluralize } from "@common/helpers/string";
import { isEqual } from "@common/helpers/comparison";
import { FiltersTypes } from "@common/types/filters/general";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { PaginatedTransactionsDataContext } from "@components/transactions/PaginatedTransactionsTable.context";
import { TransactionsTotals } from "@components/transactions/TransactionsTotals";
import { TransactionBulkModalsFromActions } from "@components/transactions/TransactionsBulkActions/TransactionBulkModalsFromActions";
import { useFooterPassingStatistics } from "@components/transactions/views/useFooterPassingStatistics";
import { useAccrualReviewBulkActions } from "@components/accounting/accrualReview/useAccrualReviewBulkActions";
import { TransactionsTaskAskButton } from "../Transactions/TransactionsTaskAskButton";
import { getAccrualTaskConfirmAction } from "./accrualTaskConfirmAction";
import {
    isEnterReservedByFocusedControl,
    useAccrualTaskRowActions,
} from "./useAccrualTaskRowActions";

import styles from "./AccrualReviewTaskActionBar.module.scss";

const NO_ACCRUAL_LEDGER_TOOLTIP =
    "Accrual ledger isn't available for this selection";

interface AccrualReviewTaskActionBarProps {
    /**
     * The task's saved view filters, used to detect whether the user has
     * added interactive filters on top of the task's defaults. When current
     * filters match these, the primary action is "Confirm all"; otherwise it
     * switches to "Confirm filtered".
     */
    initialFilters: FiltersTypes;
}

/**
 * Command-island footer for `TaskType.ACCRUAL_REVIEW` tasks: totals on the
 * left; Ask, Memo, Ledger, Actions and Confirm on the right. Memo, Ledger and
 * Actions show only for a selection; Confirm's copy and shortcut follow the
 * selection. The table loads more rows on scroll, so there is no pager.
 */
export const AccrualReviewTaskActionBar = ({
    initialFilters,
}: AccrualReviewTaskActionBarProps) => {
    const { transactions, filters } = useContext(
        PaginatedTransactionsDataContext,
    );

    const bulkActions = useAccrualReviewBulkActions();
    const passingStatistics = useFooterPassingStatistics();
    const { isViewOnly } = useViewOnlyRefusal();
    const viewOnlyTooltip = useViewOnlyTooltipReason("");

    const { setShouldShowMemo } = bulkActions;
    const openBulkMemo = useCallback(
        () => setShouldShowMemo(true),
        [setShouldShowMemo],
    );
    const { handleMemo, handleLedger, handleActions } =
        useAccrualTaskRowActions({
            selectedCount: bulkActions.selectedCount,
            isBusy: bulkActions.isBusy,
            openBulkMemo,
            openBulkAccrualLedger: bulkActions.handleSetAccrualCoaClick,
            openBulkActions: bulkActions.handleOpenBulkActions,
        });

    const haveFiltersChanged = useMemo(
        () => !isEqual(filters, initialFilters),
        [filters, initialFilters],
    );

    if (!transactions || transactions.length === 0) {
        return null;
    }

    const confirmAction = getAccrualTaskConfirmAction({
        selectedCount: bulkActions.selectedCount,
        hasSelectedAll: bulkActions.hasSelectedAll,
        haveFiltersChanged,
    });
    const isConfirmAll = confirmAction.kind === "confirmAll";
    const isConfirmDisabled =
        isViewOnly ||
        (isConfirmAll
            ? bulkActions.isConfirmAllDisabled
            : bulkActions.isBulkAcceptDisabled);

    const hasSelection = bulkActions.selectedCount > 0;
    const isEditDisabled =
        isViewOnly || bulkActions.isBusy || bulkActions.isFieldLocked;
    const isLedgerUnavailable = !bulkActions.hasAccrualCoaOptions;
    const editTooltip = isViewOnly
        ? viewOnlyTooltip
        : bulkActions.lockedSelectionTooltip;
    const ledgerTooltip =
        isLedgerUnavailable && !isEditDisabled
            ? NO_ACCRUAL_LEDGER_TOOLTIP
            : editTooltip;
    const acceptableCount =
        bulkActions.pendingClassification?.acceptableCount ?? 0;

    return (
        <>
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <div className={styles.summary}>
                    {passingStatistics && (
                        <TransactionsTotals {...passingStatistics} />
                    )}
                </div>
                <div className={styles.actions}>
                    <TransactionsTaskAskButton />
                    {hasSelection && (
                        <>
                            <WithHotkey
                                hotKey="m"
                                hotKeyDisplayPosition="inline"
                                tooltip={editTooltip}
                                hotKeyOptions={{
                                    description: "Update memo",
                                    preventDefault: true,
                                }}
                            >
                                {(hotkeyHint) => (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        className={styles.shortcutButton}
                                        onClick={handleMemo}
                                        disabled={isEditDisabled}
                                        rightIcon={hotkeyHint}
                                    >
                                        Memo
                                    </Button>
                                )}
                            </WithHotkey>
                            <WithHotkey
                                hotKey="l"
                                hotKeyDisplayPosition="inline"
                                tooltip={ledgerTooltip}
                                hotKeyOptions={{
                                    description: "Update accrual ledger",
                                    preventDefault: true,
                                }}
                            >
                                {(hotkeyHint) => (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        className={styles.shortcutButton}
                                        onClick={handleLedger}
                                        disabled={
                                            isEditDisabled ||
                                            isLedgerUnavailable
                                        }
                                        rightIcon={hotkeyHint}
                                    >
                                        Ledger
                                    </Button>
                                )}
                            </WithHotkey>
                            <Button
                                variant="primary"
                                size="md"
                                className={styles.shortcutButton}
                                onClick={handleActions}
                                disabled={bulkActions.isBusy}
                                rightIcon={
                                    <span data-slot-box="">
                                        <Shortcut keys="mod+k" />
                                    </span>
                                }
                            >
                                Actions
                            </Button>
                            <span className={styles.divider} aria-hidden />
                        </>
                    )}
                    <WithHotkey
                        hotKey={confirmAction.hotKey}
                        hotKeyDisplayPosition="inline"
                        tooltip={viewOnlyTooltip}
                        hotKeyOptions={{
                            description: confirmAction.description,
                            preventDefault: true,
                            ignoreEventWhen:
                                confirmAction.hotKey === "enter"
                                    ? isEnterReservedByFocusedControl
                                    : undefined,
                        }}
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                className={styles.shortcutButton}
                                onClick={
                                    isConfirmAll
                                        ? bulkActions.handleConfirmAll
                                        : bulkActions.handleBulkAccept
                                }
                                disabled={isConfirmDisabled}
                                rightIcon={hotkeyHint}
                            >
                                {confirmAction.label}
                            </Button>
                        )}
                    </WithHotkey>
                </div>
            </CommandIsland>

            <ConfirmationModal
                show={bulkActions.isConfirmModalVisible}
                title={`Confirm ${acceptableCount} ${pluralize("transaction", acceptableCount)}?`}
                question={bulkActions.confirmModalMessage}
                yes="Confirm"
                no="Cancel"
                onConfirm={bulkActions.handleConfirm}
                onReject={bulkActions.handleCancelConfirm}
                onHide={bulkActions.handleCancelConfirm}
            />

            <TransactionBulkModalsFromActions bulkActions={bulkActions} />
        </>
    );
};
